import { memo } from 'react';
import Icon from './Icon';

export interface ErrorFallbackProps {
	error?: Error;
	resetError?: () => void;
}

/**
 * Professional error fallback UI with navigation recovery
 * Uses window.location for navigation to avoid Router context dependencies
 */
const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
	const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

	const handleGoBack = () => {
		if (resetError) {
			resetError();
		}
		// Use window.location for reliable navigation that works even without Router context
		if (window.history.length > 1) {
			window.history.back();
		} else {
			window.location.href = '/';
		}
	};

	const handleGoHome = () => {
		if (resetError) {
			resetError();
		}
		window.location.href = '/';
	};

	const handleRetry = () => {
		if (resetError) {
			resetError();
		} else {
			window.location.reload();
		}
	};

	return (
		<div
			role="alert"
			aria-live="assertive"
			style={{
				minHeight: '60vh',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '2rem',
				textAlign: 'center',
			}}
		>
			<div style={{ marginBottom: '1.5rem', color: '#dc3545' }}>
				<Icon name="alert-circle" size={64} className="error-icon" />
			</div>
			<h1 style={{ marginBottom: '1rem', fontSize: '2rem' }}>Something went wrong</h1>
			<p style={{ marginBottom: '2rem', color: '#666', maxWidth: '600px' }}>
				We're sorry, but something unexpected happened. You can try going back to the previous
				page, returning home, or refreshing the page.
			</p>
			{error && import.meta.env.DEV && (
				<details style={{ marginBottom: '2rem', textAlign: 'left', maxWidth: '600px' }}>
					<summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>Error Details</summary>
					<pre
						style={{
							background: '#f8f9fa',
							padding: '1rem',
							borderRadius: '4px',
							overflow: 'auto',
							fontSize: '0.875rem',
						}}
					>
						{error.message}
						{error.stack && `\n\n${error.stack}`}
					</pre>
				</details>
			)}
			<div
				style={{
					display: 'flex',
					gap: '1rem',
					flexWrap: 'wrap',
					justifyContent: 'center',
				}}
			>
				{currentPath !== '/' && (
					<button
						onClick={handleGoBack}
						className="btn btn-secondary"
						aria-label="Go back to previous page"
					>
						<Icon name="arrow-left" size={18} className="btn-icon" />
						Go Back
					</button>
				)}
				<button
					onClick={handleGoHome}
					className="btn btn-primary"
					aria-label="Go to home page"
				>
					<Icon name="home" size={18} className="btn-icon" />
					Go Home
				</button>
				<button
					onClick={handleRetry}
					className="btn btn-primary"
					aria-label="Retry or refresh page"
				>
					<Icon name="refresh-cw" size={18} className="btn-icon" />
					Retry
				</button>
			</div>
		</div>
	);
};

export default memo(ErrorFallback);
